/**
 * Secure logging utility that prevents sensitive information disclosure in production
 * Part of security hardening to address information disclosure vulnerabilities
 */

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  [key: string]: any;
}

const isDevelopment = import.meta.env.DEV;
const isTest = import.meta.env.MODE === 'test';

/**
 * Safe logging function that filters sensitive data and respects environment
 */
export const secureLog = {
  info: (message: string, context?: LogContext) => {
    if (isDevelopment || isTest) {
      // eslint-disable-next-line no-console
      console.info(`[INFO] ${message}`, context ? sanitizeContext(context) : '');
    }
  },

  warn: (message: string, context?: LogContext) => {
    if (isDevelopment || isTest) {
      // eslint-disable-next-line no-console
      console.warn(`[WARN] ${message}`, context ? sanitizeContext(context) : '');
    }
  },

  error: (message: string, error?: Error | unknown, context?: LogContext) => {
    if (isDevelopment || isTest) {
      // eslint-disable-next-line no-console
      console.error(`[ERROR] ${message}`, error, context ? sanitizeContext(context) : '');
    } else {
      // In production, only log essential error information without sensitive details
      // eslint-disable-next-line no-console
      console.error(`[ERROR] ${message}`);
    }
  },

  debug: (message: string, context?: LogContext) => {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.debug(`[DEBUG] ${message}`, context ? sanitizeContext(context) : '');
    }
  }
};

/**
 * Remove sensitive information from log context
 */
function sanitizeContext(context: LogContext): LogContext {
  const sanitized = { ...context };
  
  // Remove sensitive fields
  const sensitiveFields = [
    'password', 'token', 'session', 'auth', 'key', 'secret',
    'email', 'phone', 'address', 'ip', 'userAgent'
  ];
  
  sensitiveFields.forEach(field => {
    if (sanitized[field]) {
      sanitized[field] = '[REDACTED]';
    }
  });
  
  return sanitized;
}

/**
 * Production-safe console wrapper for sensitive operations
 * Reduces information disclosure in production builds
 */
export const safeConsole = {
  log: (...args: any[]) => {
    if (isDevelopment || isTest) {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  },
  
  info: (...args: any[]) => {
    if (isDevelopment || isTest) {
      // eslint-disable-next-line no-console
      console.info(...args);
    }
  },
  
  warn: (...args: any[]) => {
    if (isDevelopment || isTest) {
      // eslint-disable-next-line no-console
      console.warn(...args);
    }
  },
  
  error: (...args: any[]) => {
    // Always allow error logging, but sanitize in production
    if (isDevelopment || isTest) {
      // eslint-disable-next-line no-console
      console.error(...args);
    } else {
      // In production, only log the first argument (error message)
      // eslint-disable-next-line no-console
      console.error(args[0]);
    }
  }
};